import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import {
  ArrowLeft, Trophy, Medal, Target, Star, Flame, Camera, MapPin, Award,
  Compass, ThumbsUp, Zap, User, Route, RefreshCw,
} from 'lucide-react';

const ICON_MAP: Record<string, any> = {
  'trophy': Trophy, 'medal': Medal, 'star': Star, 'flame': Flame,
  'camera': Camera, 'map-pin': MapPin, 'award': Award, 'compass': Compass,
  'thumbs-up': ThumbsUp, 'zap': Zap, 'target': Target,
};

type Tab = 'ranking' | 'badges' | 'metas';

export default function Achievements() {
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState<Tab>('ranking');

  const { data: myStats } = useQuery({
    queryKey: ['gamification', 'my-stats'],
    queryFn: () => api.get('/gamification/my-stats').then(r => r.data.data),
  });

  const { data: leaderboard } = useQuery({
    queryKey: ['gamification', 'leaderboard'],
    queryFn: () => api.get('/gamification/leaderboard?limit=50').then(r => r.data.data),
    enabled: activeTab === 'ranking',
  });

  const { data: allBadges } = useQuery({
    queryKey: ['gamification', 'badges'],
    queryFn: () => api.get('/gamification/badges').then(r => r.data.data),
    enabled: activeTab === 'badges',
  });

  const earnedCodes = new Set((myStats?.badges || []).map((b: any) => b.code));

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-br from-yellow-500 to-orange-500 text-white p-4 pb-20">
        <div className="flex items-center gap-4 mb-4">
          <button onClick={() => setLocation('/')} className="p-2 -ml-2 rounded-full hover:bg-white/20">
            <ArrowLeft className="h-6 w-6" />
          </button>
          <h1 className="text-lg font-bold">Conquistas</h1>
        </div>

        {/* Summary Card */}
        <div className="bg-white rounded-xl p-4 text-gray-900 shadow-lg -mb-16">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-yellow-600">
                #{myStats?.ranking?.rank_by_score || '-'}
              </p>
              <p className="text-xs text-gray-600">Posicao</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">
                {myStats?.total_points || 0}
              </p>
              <p className="text-xs text-gray-600">Pontos</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">
                {myStats?.badge_count || 0}
              </p>
              <p className="text-xs text-gray-600">Badges</p>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="px-4 pt-20 pb-2">
        <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
          {(['ranking', 'badges', 'metas'] as Tab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              }`}
            >
              {tab === 'ranking' ? 'Ranking' : tab === 'badges' ? 'Badges' : 'Metas'}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 p-4 pb-20">
        {activeTab === 'ranking' && (
          <div className="space-y-2">
            {(leaderboard || []).map((p: any, i: number) => (
              <div
                key={p.promoter_id}
                className={`flex items-center gap-3 p-3 rounded-xl ${
                  myStats?.ranking?.promoter_name === p.promoter_name
                    ? 'bg-yellow-50 border-2 border-yellow-400'
                    : 'bg-white border border-gray-100'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  i === 0 ? 'bg-yellow-400 text-white' :
                  i === 1 ? 'bg-gray-300 text-white' :
                  i === 2 ? 'bg-amber-600 text-white' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm truncate">{p.promoter_name}</p>
                  <p className="text-xs text-gray-500">{p.region_name || 'Sem regiao'}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-sm text-gray-900">{parseFloat(p.avg_score).toFixed(0)}%</p>
                  <p className="text-xs text-gray-500">{p.total_visits_30d} visitas</p>
                </div>
                <div className="flex items-center gap-1 text-yellow-500">
                  <Trophy className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">{p.badge_count}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'badges' && (
          <div className="grid grid-cols-3 gap-3">
            {(allBadges || []).map((badge: any) => {
              const earned = earnedCodes.has(badge.code);
              const IconComp = ICON_MAP[badge.icon] || Trophy;
              return (
                <div
                  key={badge.code}
                  className={`flex flex-col items-center p-3 rounded-xl text-center ${
                    earned ? 'bg-yellow-50 border-2 border-yellow-400' : 'bg-gray-100 border border-gray-200 opacity-50'
                  }`}
                >
                  <div className={`p-2 rounded-full mb-2 ${
                    earned ? 'bg-yellow-400 text-white' : 'bg-gray-300 text-gray-500'
                  }`}>
                    <IconComp className="h-6 w-6" />
                  </div>
                  <p className="text-xs font-semibold text-gray-900 leading-tight">{badge.name}</p>
                  <p className="text-[10px] text-gray-500 mt-1">{badge.points}pts</p>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'metas' && (
          <div className="space-y-3">
            {(myStats?.goals || []).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Target className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Nenhuma meta ativa</p>
              </div>
            ) : (
              (myStats?.goals || []).map((goal: any) => {
                const progress = goal.target_value > 0
                  ? Math.min((goal.current_value / goal.target_value) * 100, 100)
                  : 0;
                const isCompleted = goal.completed_at != null;

                return (
                  <div key={goal.id} className={`p-4 rounded-xl ${
                    isCompleted ? 'bg-green-50 border-2 border-green-400' : 'bg-white border border-gray-100'
                  }`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 text-sm">{goal.title}</p>
                        {goal.description && (
                          <p className="text-xs text-gray-500 mt-0.5">{goal.description}</p>
                        )}
                      </div>
                      {isCompleted && <Star className="h-5 w-5 text-green-500 flex-shrink-0" />}
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all rounded-full ${
                            isCompleted ? 'bg-green-500' : 'bg-yellow-500'
                          }`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-700">
                        {goal.current_value}/{goal.target_value}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Ate {new Date(goal.period_end).toLocaleDateString('pt-BR')}</span>
                      {goal.reward_description && (
                        <span className="text-yellow-600 font-medium">{goal.reward_description}</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-2">
        <div className="flex items-center justify-around">
          <button onClick={() => setLocation('/')} className="flex flex-col items-center gap-1 p-2 text-gray-500">
            <MapPin className="h-6 w-6" />
            <span className="text-xs">Inicio</span>
          </button>
          <button onClick={() => setLocation('/route')} className="flex flex-col items-center gap-1 p-2 text-gray-500">
            <Route className="h-6 w-6" />
            <span className="text-xs">Roteiro</span>
          </button>
          <button onClick={() => setLocation('/sync')} className="flex flex-col items-center gap-1 p-2 text-gray-500">
            <RefreshCw className="h-6 w-6" />
            <span className="text-xs">Sync</span>
          </button>
          <button className="flex flex-col items-center gap-1 p-2 text-yellow-500">
            <Trophy className="h-6 w-6" />
            <span className="text-xs font-medium">Ranking</span>
          </button>
          <button onClick={() => setLocation('/profile')} className="flex flex-col items-center gap-1 p-2 text-gray-500">
            <User className="h-6 w-6" />
            <span className="text-xs">Perfil</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
